import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useThemeSwitcher } from "react-css-theme-switcher";

import { icon, label, number, quote } from "../../../../config";
import { filterData } from "../../../../utils/kendo";
import { DrutasDropdown } from "../../../DrutasDropdown/DrutasDropdown";
import { assignedTypes } from "../../../Tasks/tasks.constants";
import {
  getAssignedIdElem,
  getAssigneeData,
  getFilterAssigneeList,
  getProjectList,
  handleGetProjectWorkflows,
  isUserPartOfProject,
} from "../../sidebar.helper";
import { getApprovalRelatedAssignId } from "./approval.helper";

/**
 * ApprovalAssignmentIdNew component - Merged DD1 + DD2
 * Parent component => ApprovalAssignment
 * @returns JSX
 * @author Sejal
 */
const ApprovalAssignmentIdNew = (props) => {
  const {
    approvalAssignType,
    setApprovalAssignType,
    approvalAssignId,
    setApprovalAssignId,
    setApprovalRelatedAssignedType,
    setApprovalRelatedAssignedId,
    setRelatedAssignedIdList,
    approvalSelection,
    setApprovalSelection,
    disable,
  } = props;

  const [assignedIdList, setAssignedIdList] = useState([]);
  const [filterText, setFilterText] = useState("");
  const { currentTheme } = useThemeSwitcher();

  const { task } = useSelector((state) => state.taskSidebar);
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.auth);

  const dynamicPlaceholder = useMemo(() => {
    if (approvalAssignType?.key === number.TWO) return quote.SEARCH_USER;
    if (approvalAssignType?.key === number.THREE) return quote.SEARCH_TEAM;
    if (approvalAssignType?.key === number.FOUR) return quote.SEARCH_TEAM_USER;
    return quote.SEARCH_TEAM_USER;
  }, [approvalAssignType?.key]);

  // Set DD2 list based on DD1 (approvalAssignType)
  useEffect(() => {
    if (approvalAssignType) {
      getAndFilterAssignIdList();
    }
  }, [approvalAssignType]);

  /**
   * Sets assignedId list on the basis of assignmentType
   * @param {Object} event
   * @author Sejal
   */
  const getAndFilterAssignIdList = (event) => {
    setFilterText(event?.target?.value ? event?.target?.value : "");
    switch (approvalAssignType?.key) {
      case number.TWO:
        const customFilterUser = event
          ? {
              logic: "or",
              filters: [
                { field: "Email", operator: "contains", value: event?.target?.value },
                { field: "label", operator: "contains", value: event?.target?.value },
              ],
            }
          : null;
        let assigneeList = getFilterAssigneeList();
        assigneeList = assigneeList?.map((assignee) => ({ ...assignee, AssignmentType: number.TWO }));
        assigneeList = event ? filterData(customFilterUser, assigneeList) : assigneeList;
        setAssignedIdList(assigneeList);
        break;
      case number.THREE:
        const customFilterProject = event
          ? {
              logic: "or",
              filters: [
                { field: "ProjectCode", operator: "contains", value: event.target.value },
                { field: "label", operator: "contains", value: event.target.value },
              ],
            }
          : null;
        let projects = getProjectList();
        projects = projects?.map((project) => ({ ...project, AssignmentType: number.THREE }));
        projects = event ? filterData(customFilterProject, projects) : projects;
        setAssignedIdList(projects);
        break;
      case number.FOUR:
        const customFilterHybrid = event
          ? {
              logic: "or",
              filters: [
                { field: "Email", operator: "contains", value: event.target.value },
                { field: "label", operator: "contains", value: event.target.value },
                { field: "ProjectCode", operator: "contains", value: event.target.value },
              ],
            }
          : null;
        let assignees = getFilterAssigneeList();
        assignees = assignees?.map((assignee) => ({ ...assignee, AssignmentType: number.TWO }));

        let teams = getProjectList();
        teams = teams?.map((project) => ({ ...project, AssignmentType: number.THREE }));

        let lists = [...teams, ...assignees];
        lists = event ? filterData(customFilterHybrid, lists) : lists;
        setAssignedIdList(lists);
        break;
      default:
        break;
    }
  };

  /**
   * Makes required changes when assignId is changed
   * @param {Object} selectedAssignId
   * @author Sejal
   */
  const assignIdStateChanges = async (selectedAssignId) => {
    setApprovalAssignId(selectedAssignId);
    const requiredWorkflowAssignmentType = defaultDetails?.relatedAssignmentTypeList?.find(
      (type) => type.value == (isUserPartOfProject(defaultDetails, selectedAssignId) ? number.TWO : number.FIVE)
    );
    setApprovalRelatedAssignedType(requiredWorkflowAssignmentType ? requiredWorkflowAssignmentType : null);
  };

  /**
   * Handles assignedId change
   * @param {Object} selectedItem
   * @author Sejal  
   */
  const handleChange = async (selectedItem) => {
    if (!selectedItem) return;

    await assignIdStateChanges(selectedItem);

    // Fetch project workflows based on assignment type
    const projectId = selectedItem?.AssignmentType === number.TWO 
      ? parseInt(selectedItem?.myProjectId) 
      : parseInt(selectedItem?.value);
    
    await handleGetProjectWorkflows(projectId);
    // Update DD3 and DD4 based on selection
    const workflowAssignmentType = defaultDetails?.relatedAssignmentTypeList?.find(
      (type) => type.value == (isUserPartOfProject(defaultDetails, selectedItem) ? number.TWO : number.FIVE)
    );
    setApprovalRelatedAssignedType(workflowAssignmentType ? workflowAssignmentType : null);

    // Build combined list of users and workflows for DD4
    const isUserPart = isUserPartOfProject(defaultDetails, selectedItem);
    
    // Get assignee list (users)
    let assigneeList = getAssigneeData(selectedItem, defaultDetails);
    assigneeList = assigneeList?.map((assignee) => ({
      ...assignee,
      RelatedAssignmentType: number.TWO,
    })) || [];

    // Get workflow list
    const projectIdForWorkflows = selectedItem?.AssignmentType === number.TWO
      ? selectedItem?.myProjectId
      : selectedItem?.value;
    
    let workflows = await handleGetProjectWorkflows(parseInt(projectIdForWorkflows));
    workflows = workflows?.filter((workflow) => workflow.IsActive);
    workflows = workflows?.map(({ WorkflowId, WorkflowName, Icon, WorkflowPrivacy, IsDefault }) => ({
      value: WorkflowId,
      label: WorkflowName,
      privacy: WorkflowPrivacy,
      icon: Icon,
      isDefault: IsDefault,
      RelatedAssignmentType: number.FIVE,
    })) || [];

    // Combine based on user's project membership
    let combinedList = [];
    if (isUserPart) {
      combinedList = [...workflows, ...assigneeList];
    } else {
      combinedList = [...workflows];
    }
    setRelatedAssignedIdList(combinedList);

    const relatedAssignIdPayload = {
      workflowAssignedType: workflowAssignmentType,
      selectedAssignId: selectedItem,
      defaultDetails,
      isNewTask: false,
      user,
      requiredRelatedAssignmentList: combinedList,
      task,
    };
    const relatedAssignedId = await getApprovalRelatedAssignId(relatedAssignIdPayload);
    setApprovalRelatedAssignedId(relatedAssignedId ? relatedAssignedId : null);
  };

  /**
   * Handles User and Team filter logic
   * @param {String} filterType
   * @author Sejal
   */
  const handleFilter = (filterType) => {
    if (filterType === "user") {
      if (approvalSelection?.includes("user") && approvalSelection.length === number.TWO) {
        const newSelections = approvalSelection?.filter((item) => item !== filterType);
        setApprovalAssignType(assignedTypes?.find((type) => type.key === number.THREE));
        setApprovalSelection(newSelections);
      } else {
        if (approvalSelection?.includes("user")) return;
        const newSelections = [...approvalSelection, filterType];
        if (newSelections.length === 2) {
          setApprovalAssignType(assignedTypes?.find((type) => type.key === number.FOUR));
        }
        setApprovalSelection(newSelections);
      }
      return;
    }

    if (filterType === "team") {
      if (approvalSelection?.includes("team") && approvalSelection.length === number.TWO) {
        const newSelections = approvalSelection?.filter((item) => item !== filterType);
        setApprovalAssignType(assignedTypes?.find((type) => type.key === number.TWO));
        setApprovalSelection(newSelections);
      } else {
        if (approvalSelection?.includes("team")) return;
        const newSelections = [...approvalSelection, filterType];
        if (newSelections.length === 2) {
          setApprovalAssignType(assignedTypes?.find((type) => type.key === number.FOUR));
        }
        setApprovalSelection(newSelections);
      }
      return;
    }
  };

  const clearFilterText = () => {
    getAndFilterAssignIdList();
  };

  /**
   * Custom dropdown Item render for DD2
   * @returns JSX
   * @author Cursor AI
   */
  const itemRenderNew = (item, handleOnClick) => {
    return (
      <>
        {item?.AssignmentType === number.TWO ? (
          <div
            className="d-flex align-items-center drutas-dropdown-popup-element px-3 py-2 truncate-text cursor-pointer"
            onClick={() => handleOnClick(item)}
          >
            <div className="profile-picture-container mr-3">
              {item?.PhotoLinks?.small ? (
                <img src={item?.PhotoLinks?.small} className="profile-picture" />
              ) : (
                <span>{icon.USER_ICON}</span>
              )}
            </div>
            <div className="truncate-text d-flex align-items-center">
              <div className="truncate-text">
                <div className="assignee-name truncate-text">{item?.label}</div>
                <div className="assignee-email truncate-text">{item?.Email}</div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="d-flex align-items-center drutas-dropdown-popup-element px-3 py-2 truncate-text cursor-pointer"
            onClick={() => handleOnClick(item)}
          >
            <div
              className={`profile-picture-container mr-3 ${
                item?.Privacy == number.ONE ? "dark-blue-icon" : item?.Privacy == number.TWO ? "sky-blue-icon" : "black-icon"
              }`}
            >
              {icon.PROJECT_ICON}
            </div>
            <div className="truncate-text d-flex align-items-center">
              <div className="truncate-text">
                <div className="assignee-name truncate-text">{item?.label}</div>
                <div className="assignee-email truncate-text">{item?.ProjectCode}</div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  /**
   * Custom dropdown value render for DD2
   * @returns JSX
   * @author Sejal
   */
  const valueRenderNew = (item) => {
    return (
      <>
        {item?.AssignmentType === number.TWO ? (
          <div className="d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text">
            <div className="d-flex align-items-center truncate-text">
              <div className="image-box mr-3">
                {item?.PhotoLinks?.small ? (
                  <img src={item?.PhotoLinks?.small} />
                ) : (
                  <span className="assignee-icon">{icon.USER_ICON}</span>
                )}
              </div>
              <div className="truncate-text">
                <div className="assignee-name truncate-text">{item?.label}</div>
              </div>
            </div>
            <span>{icon.ARROW_DROP_DOWN_ICON}</span>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between drutas-dropdown-popup-element truncate-text">
            <div className="d-flex align-items-center">
              <div
                className={`image-box mr-3 ${
                  item?.Privacy == number.ONE ? "dark-blue-icon" : item?.Privacy == number.TWO ? "sky-blue-icon" : "black-icon"
                }`}
              >
                {icon.PROJECT_ICON}
              </div>
              <div className="truncate-text">
                <div className="assignee-name truncate-text">{item?.label}</div>
              </div>
            </div>
            <span>{icon.ARROW_DROP_DOWN_ICON}</span>
          </div>
        )}
      </>
    );
  };

  const filtersContent = () => {
    return (
      <>
        <div className="p-3 pb-2">
          <div className="d-flex align-items-center justify-content-between">
            <span>Search with</span>
            <div className="assignto-toggle-btn">
              <button
                className={`btn assignto-toggle-first ${approvalSelection?.includes("team") ? "btn-primary" : ""}`}
                onClick={() => handleFilter("team")}
              >
                {icon.GROUP} Teams
              </button>
              <button
                className={`btn assignto-toggle-second ${approvalSelection?.includes("user") ? "btn-primary" : ""}`}
                onClick={() => handleFilter("user")}
              >
                {icon.TASK_PERSON} Users
              </button>
            </div>
          </div>
          <div className="assignto-search mt-3 position-relative">
            <span className="assignto-search-icon">{icon.SEARCH}</span>
            <input
              autoFocus={true}
              className="assignto-search-field form-control w-100"
              placeholder={dynamicPlaceholder}
              value={filterText}
              onChange={getAndFilterAssignIdList}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <React.Fragment>
      <div className="form-group col-lg-6 col-md-6 col-sm-6 d-flex align-items-left flex-column task-returnable">
        <div className="d-flex mt-auto">
          <div className="w-100">
            <div className="d-flex">
              <div className="task-assignee w-100">
                <div>
                  <div className="d-flex align-items-end justify-content-between mb-1">
                    <label className="mr-2 mb-0" htmlFor="">{label.ASSIGN_TO}</label>
                  </div>
                  <DrutasDropdown
                    value={approvalAssignId}
                    onChange={handleChange}
                    data={assignedIdList}
                    label={"label"}
                    key={"value"}
                    isfilter={true}
                    filterContent={filtersContent}
                    itemRender={itemRenderNew}
                    valueRender={valueRenderNew}
                    disabled={disable}
                    popupClass="approval-dd-height"
                    clearFilterText={clearFilterText}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ApprovalAssignmentIdNew;

